/*
 * Soft illustration panels that stand in for the photos on the testimonial
 * cards (mockup 00.24.07): a white office block with glass bands, a blue
 * glass tower, and an old-town skyline over a river with an arched bridge.
 * Decorative only (aria-hidden); the card text carries the content.
 */
import { useId } from 'react';

function Sky({ id, from, to }) {
  return (
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor={from} />
      <stop offset="1" stopColor={to} />
    </linearGradient>
  );
}

function Cloud({ x, y, s = 1, opacity = 0.9 }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`} fill="#fff" opacity={opacity}>
      <ellipse cx="0" cy="8" rx="26" ry="9" />
      <ellipse cx="-10" cy="2" rx="12" ry="10" />
      <ellipse cx="8" cy="-1" rx="15" ry="13" />
    </g>
  );
}

/* A clump of round trees, darker underneath. */
function Trees({ x, y, s = 1 }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <ellipse cx="0" cy="40" rx="62" ry="22" fill="#3f8a3c" />
      <circle cx="-34" cy="18" r="22" fill="#5aa64a" />
      <circle cx="-6" cy="6" r="26" fill="#6db556" />
      <circle cx="26" cy="16" r="22" fill="#5aa64a" />
      <circle cx="44" cy="30" r="16" fill="#4b9a42" />
      <circle cx="-14" cy="0" r="10" fill="#8acb6a" opacity="0.8" />
      <circle cx="18" cy="6" r="8" fill="#8acb6a" opacity="0.7" />
    </g>
  );
}

/* agilus: a white office block in perspective, glass bands, a sign on top. */
function Office({ uid }) {
  const sky = `${uid}-sky`;
  const glass = `${uid}-glass`;
  const side = `${uid}-side`;
  return (
    <>
      <defs>
        <Sky id={sky} from="#8fbcec" to="#e6f1fb" />
        <linearGradient id={glass} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2f5f86" />
          <stop offset="0.5" stopColor="#5f93b8" />
          <stop offset="1" stopColor="#1d4466" />
        </linearGradient>
        <linearGradient id={side} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#dde3ea" />
          <stop offset="1" stopColor="#f7f9fb" />
        </linearGradient>
      </defs>
      <rect width="260" height="300" fill={`url(#${sky})`} />
      <Cloud x={150} y={40} s={1.3} />
      <Cloud x={226} y={92} s={0.8} opacity={0.75} />
      {/* left, receding wall */}
      <path d="M78 120 128 72v228H78Z" fill={`url(#${side})`} />
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <path key={`l-${i}`} d={`M84 ${150 + i * 26} 122 ${128 + i * 26}v12L84 ${162 + i * 26}Z`} fill="#c9d3de" />
      ))}
      {/* main facade */}
      <path d="M128 72 260 20v280H128Z" fill="#f4f6f8" />
      <path d="M128 72 260 20v10L128 82Z" fill="#dfe5ec" />
      {/* the sign */}
      <g transform="rotate(-21 196 70)">
        <text x="146" y="88" fill="#1f5fbf" fontFamily="Outfit, Arial, sans-serif" fontSize="30" fontWeight="700">
          agilus
        </text>
        <text x="172" y="102" fill="#1f5fbf" fontFamily="Inter, Arial, sans-serif" fontSize="10" letterSpacing="2">
          diagnostics
        </text>
        <g fill="none" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M236 70l7 7-7 7" stroke="#f29a1f" />
          <path d="M244 70l7 7-7 7" stroke="#e5484d" />
          <path d="M252 70l7 7-7 7" stroke="#1fa463" />
        </g>
      </g>
      {/* glass bands with mullions */}
      <path d="M140 160 260 128v46l-120 26Z" fill={`url(#${glass})`} />
      <path d="M140 222 260 196v54l-120 22Z" fill={`url(#${glass})`} />
      {[160, 180, 200, 220, 240].map((x) => (
        <path
          key={`m-${x}`}
          d={`M${x} ${160 - (x - 140) * 0.27}v42M${x} ${222 - (x - 140) * 0.22}v50`}
          stroke="#e8eef4"
          strokeWidth="2"
        />
      ))}
      <path d="M140 160 260 128v6l-120 32Z" fill="#fff" opacity="0.35" />
      {[0, 1, 2].map((i) => (
        <path key={`s-${i}`} d={`M128 ${110 + i * 16} 260 ${62 + i * 16}`} stroke="#e1e6ec" strokeWidth="1.4" />
      ))}
      <Trees x={70} y={262} s={1.05} />
      <Trees x={232} y={276} s={0.8} />
    </>
  );
}

/* MAX Lab: a blue glass tower with a sign, trees in front. */
function GlassTower({ uid }) {
  const sky = `${uid}-sky`;
  const glass = `${uid}-glass`;
  const shine = `${uid}-shine`;
  return (
    <>
      <defs>
        <Sky id={sky} from="#7fb1ea" to="#e8f2fc" />
        <linearGradient id={glass} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3f86d8" />
          <stop offset="0.45" stopColor="#1f5fae" />
          <stop offset="1" stopColor="#0f3f7c" />
        </linearGradient>
        <linearGradient id={shine} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="0.5" stopColor="#d7ecff" stopOpacity="0.45" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect width="260" height="300" fill={`url(#${sky})`} />
      <Cloud x={120} y={52} s={1.2} />
      <Cloud x={60} y={110} s={0.7} opacity={0.7} />
      <path d="M104 118 136 96v204h-32Z" fill="#2c5c99" />
      <path d="M136 96 260 70v230H136Z" fill={`url(#${glass})`} />
      {Array.from({ length: 12 }, (_, i) => (
        <path key={`h-${i}`} d={`M136 ${112 + i * 16} 260 ${88 + i * 16}`} stroke="#9fc8f2" strokeWidth="1" opacity="0.55" />
      ))}
      {[156, 176, 196, 216, 236].map((x) => (
        <path key={`v-${x}`} d={`M${x} ${96 - (x - 136) * 0.21}V300`} stroke="#9fc8f2" strokeWidth="1" opacity="0.4" />
      ))}
      {Array.from({ length: 10 }, (_, i) => (
        <path key={`sh-${i}`} d={`M104 ${130 + i * 17}l32-20`} stroke="#6f9bd1" strokeWidth="1" opacity="0.5" />
      ))}
      <path d="M170 96 220 82v218h-50Z" fill={`url(#${shine})`} />
      <g transform="rotate(-12 200 112)">
        <path d="M150 104h12v10c0 5-6 8-6 8s-6-3-6-8Z" fill="#fff" />
        <circle cx="156" cy="111" r="2.6" fill="#1f5fae" />
        <text x="166" y="120" fill="#fff" fontFamily="Outfit, Arial, sans-serif" fontSize="22" fontWeight="800">
          MAX
        </text>
        <text x="215" y="120" fill="#fff" fontFamily="Outfit, Arial, sans-serif" fontSize="22" fontWeight="500">
          Lab
        </text>
      </g>
      <Trees x={110} y={256} s={1.15} />
      <Trees x={250} y={282} s={0.7} />
    </>
  );
}

const ROOFS = [
  [64, 150, 18, 12],
  [86, 146, 16, 14],
  [106, 152, 20, 10],
  [128, 144, 16, 16],
  [148, 150, 18, 12],
  [170, 146, 18, 14],
  [192, 152, 16, 10],
  [212, 146, 20, 14],
  [236, 150, 20, 12],
];

/* SPADIA: an old-town skyline on a hill, an arched bridge, a calm river. */
function OldTown({ uid }) {
  const sky = `${uid}-sky`;
  const water = `${uid}-water`;
  return (
    <>
      <defs>
        <linearGradient id={sky} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#bcd6ef" />
          <stop offset="0.55" stopColor="#f3e2c8" />
          <stop offset="1" stopColor="#f6d6a8" />
        </linearGradient>
        <linearGradient id={water} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#9fb9cc" />
          <stop offset="1" stopColor="#5f8199" />
        </linearGradient>
      </defs>
      <rect width="260" height="300" fill={`url(#${sky})`} />
      <Cloud x={90} y={40} s={0.9} opacity={0.7} />
      <path d="M40 170c30-30 70-50 120-52s80 14 100 24v40H40Z" fill="#7b8f63" />
      {/* castle and cathedral spires */}
      <path d="M96 128h120v26H96Z" fill="#e9d7bc" />
      {Array.from({ length: 12 }, (_, i) => (
        <rect key={`cw-${i}`} x={100 + i * 9.6} y="134" width="4" height="6" fill="#b69a74" />
      ))}
      <path d="M150 128V86l6-10 6 10v42Z" fill="#8a7a66" />
      <path d="M150 86l6-30 6 30Z" fill="#4c4a46" />
      <path d="M170 128V96l4-8 4 8v32Z" fill="#8a7a66" />
      <path d="M170 96l4-22 4 22Z" fill="#4c4a46" />
      <path d="M138 128v-22l3-6 3 6v22Z" fill="#8a7a66" />
      <path d="M138 106l3-16 3 16Z" fill="#4c4a46" />
      <path d="M120 128v-10h12v10Z" fill="#d6c3a4" />
      <path d="M118 118l8-8 8 8Z" fill="#b5532f" />
      {/* red-roofed houses */}
      {ROOFS.map(([x, y, w, h]) => (
        <g key={`r-${x}`}>
          <rect x={x} y={y} width={w} height={186 - y} fill="#efe3cf" />
          <path d={`M${x - 2} ${y}l${w / 2 + 2} ${-h}l${w / 2 + 2} ${h}Z`} fill="#c4552f" />
          <rect x={x + 4} y={y + 6} width="3" height="5" fill="#a88e6c" />
          <rect x={x + w - 7} y={y + 6} width="3" height="5" fill="#a88e6c" />
        </g>
      ))}
      <path d="M30 186h230v6H30Z" fill="#8c7a63" />
      {/* bridge tower */}
      <path d="M40 140h24v62H40Z" fill="#6f665c" />
      <path d="M38 140l14-22 14 22Z" fill="#3f3b37" />
      <rect x="47" y="150" width="10" height="16" rx="5" fill="#3f3b37" />
      {/* arched bridge */}
      <path d="M40 196h220v14H40Z" fill="#8b7d6b" />
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <path key={`a-${i}`} d={`M${48 + i * 36} 232v-14a14 14 0 0 1 28 0v14`} fill="#7a6c5c" />
      ))}
      <path d="M40 210h220v8H40Z" fill="#7a6c5c" />
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <path key={`ar-${i}`} d={`M${52 + i * 36} 232v-12a10 10 0 0 1 20 0v12Z`} fill="#c6d3dc" opacity="0.7" />
      ))}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <rect key={`st-${i}`} x={54 + i * 27} y="186" width="3" height="10" fill="#4f4840" />
      ))}
      {/* river with reflections */}
      <path d="M0 232h260v68H0Z" fill={`url(#${water})`} />
      <path d="M40 236h220v10H40Z" fill="#6f665c" opacity="0.25" />
      {[0, 1, 2, 3, 4].map((i) => (
        <path
          key={`w-${i}`}
          d={`M${20 + i * 46} ${252 + (i % 2) * 16}h${30 + (i % 3) * 8}`}
          stroke="#e6eef4"
          strokeWidth="1.6"
          opacity="0.55"
          strokeLinecap="round"
        />
      ))}
      <path d="M212 300v-30h14v30Z" fill="#5a5148" />
      <path d="M210 270l9-14 9 14Z" fill="#3f3b37" />
    </>
  );
}

const ART = { tower: Office, glass: GlassTower, city: OldTown };

export function TestimonialArt({ kind }) {
  const uid = useId();
  const Art = ART[kind];
  return (
    <span className={`clients-art clients-art--${kind}`} aria-hidden="true">
      <svg viewBox="0 0 260 300" preserveAspectRatio="xMaxYMax slice" focusable="false">
        <Art uid={uid} />
      </svg>
    </span>
  );
}
